import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

type AcessoOutroSistema = {
  id: string;
  sistema: string;
  sistemaId?: string;
};

type Row = {
  id: string;
  sistema: string;
  sistemaId?: string;
};

type Sistema = {
  id: string;
  nome: string;
};

type Props = {
  initialRows?: AcessoOutroSistema[];
  onAcessoOutrosSistemasChange?: (
    acessoOutrosSistemas: AcessoOutroSistema[]
  ) => void;
  availableSistemas?: Sistema[];
  currentComponenteId?: string;
  isLoadingSistemas?: boolean;
};

const convertToRows = (acessoOutrosSistemas: AcessoOutroSistema[]): Row[] => {
  return acessoOutrosSistemas.map((item) => ({
    id: item.id,
    sistema: item.sistema,
    sistemaId: item.sistemaId,
  }));
};

export const ListaAcessoOutrosSistemas: React.FC<Props> = ({
  initialRows = [],
  onAcessoOutrosSistemasChange,
  availableSistemas = [],
  currentComponenteId,
  isLoadingSistemas = false,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<{
    sistema: string;
    sistemaId: string;
  }>({
    sistema: '',
    sistemaId: '',
  });

  useEffect(() => {
    if (initialRows.length > 0) {
      setRows(convertToRows(initialRows));
    } else {
      setRows([]);
    }
  }, [initialRows]);

  const handleEdit = (row: Row) => {
    setEditingRowId(row.id);
    setEditingValues({
      sistema: row.sistema,
      sistemaId: row.sistemaId || '',
    });
  };

  const handleDelete = (id: string) => {
    const newRows = rows.filter((r) => r.id !== id);
    setRows(newRows);
    if (onAcessoOutrosSistemasChange) {
      const acessoOutrosSistemas = newRows.map((row) => ({
        id: row.id,
        sistema: row.sistema,
        sistemaId: row.sistemaId,
      }));
      onAcessoOutrosSistemasChange(acessoOutrosSistemas);
    }
  };

  const handleAddNew = () => {
    const newRow: Row = {
      id: String(Date.now()),
      sistema: '',
      sistemaId: '',
    };
    const newRows = [newRow, ...rows];
    setRows(newRows);
    setEditingRowId(newRow.id);
    setEditingValues({ sistema: '', sistemaId: '' });
  };

  const handleSave = (id: string) => {
    if (!editingValues.sistemaId || !editingValues.sistema.trim()) {
      // Se os campos obrigatórios estiverem vazios, remove a linha (caso seja uma nova linha)
      handleDelete(id);
      return;
    }

    const newRows = rows.map((r) =>
      r.id === id
        ? {
          ...r,
          sistema: editingValues.sistema,
          sistemaId: editingValues.sistemaId,
        }
        : r
    );

    setRows(newRows);
    if (onAcessoOutrosSistemasChange) {
      const acessoOutrosSistemas = newRows.map((row) => ({
        id: row.id,
        sistema: row.sistema,
        sistemaId: row.sistemaId,
      }));
      onAcessoOutrosSistemasChange(acessoOutrosSistemas);
    }
    setEditingRowId(null);
    setEditingValues({ sistema: '', sistemaId: '' });
  };

  const handleCancel = () => {
    // Se estamos editando uma linha vazia (nova), remove ela
    if (
      editingRowId &&
      rows.find((r) => r.id === editingRowId)?.sistema === ''
    ) {
      handleDelete(editingRowId);
    }
    setEditingRowId(null);
    setEditingValues({ sistema: '', sistemaId: '' });
  };

  const handleSistemaChange = (sistemaId: string) => {
    const selectedSistema = availableSistemas.find((s) => s.id === sistemaId);
    setEditingValues({
      sistemaId,
      sistema: selectedSistema?.nome || '',
    });
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="secondary"
          size="small"
          onClick={handleAddNew}
          startIcon={<AddIcon />}
          sx={{ minWidth: '100px' }}
          disabled={isLoadingSistemas}
        >
          {isLoadingSistemas ? 'Carregando...' : 'Novo'}
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={0} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '120px' }}>Ações</TableCell>
              <TableCell>Sistema</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={2}
                  align="center"
                  sx={{ py: 3, color: 'text.secondary' }}
                >
                  Nenhum acesso a sistema configurado
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    {editingRowId === row.id ? (
                      <>
                        <Tooltip title="Salvar">
                          <IconButton
                            size="small"
                            onClick={() => handleSave(row.id)}
                            color="primary"
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancelar">
                          <IconButton
                            size="small"
                            onClick={handleCancel}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(row)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingRowId === row.id ? (
                      <FormControl fullWidth size="small" variant="outlined">
                        <Select
                          value={editingValues.sistemaId}
                          onChange={(e) => handleSistemaChange(e.target.value)}
                          displayEmpty
                          autoFocus
                          disabled={isLoadingSistemas}
                          endAdornment={
                            isLoadingSistemas ? (
                              <CircularProgress size={20} sx={{ mr: 2 }} />
                            ) : null
                          }
                        >
                          <MenuItem value="">
                            <em>
                              {isLoadingSistemas
                                ? 'Carregando sistemas...'
                                : availableSistemas.length === 0
                                  ? 'Nenhum sistema disponível'
                                  : 'Selecione um sistema'}
                            </em>
                          </MenuItem>
                          {!isLoadingSistemas &&
                            availableSistemas.map((sistema) => (
                              <MenuItem key={sistema.id} value={sistema.id}>
                                {sistema.nome}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    ) : (
                      row.sistema
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
